import {
  getRegistryRegionFromEecs,
  isEecsAccountIdentifier,
} from "@/utils/eecs";
import type { CnmcRecipient, CnmcRecipientDraft, CnmcRoute } from "./cnmcTypes";

/** A comercializador's census code, as `normalize_cnmc_company_id` accepts it. */
const CNMC_R2_PATTERN = /^R2-\d+$/;

/** The longest recipient name the CNMC forms take. */
const CNMC_RECIPIENT_NAME_MAX = 128;

/** Trims and upper-cases a typed CNMC code the way the backend normalizes it. */
export const normalizeCnmcCode = (value: string | null | undefined): string =>
  (value ?? "").trim().toUpperCase();

export const isValidR2 = (value: string | null | undefined): boolean =>
  CNMC_R2_PATTERN.test(normalizeCnmcCode(value));

/** The recipient fields a CNMC form takes. */
export type CnmcRecipientField = "name" | "r2" | "eecs";

/** The fields a recipient of each route files: a domestic one an R2 code, an export one an EECS account. */
export const CNMC_RECIPIENT_FIELDS: Record<
  CnmcRoute,
  readonly CnmcRecipientField[]
> = {
  DOMESTIC: ["name", "r2"],
  EXPORT: ["name", "eecs"],
};

/**
 * One rule a filled-in recipient field must pass, with the i18n key (and params) of its message.
 * An empty field is not checked here: the editor's `required` and the filing gate handle that.
 */
export interface CnmcRecipientCheck {
  test: (value: string) => boolean;
  message: string;
  params?: Record<string, unknown>;
}

/**
 * The checks of each field a recipient of this route files (see CNMC_RECIPIENT_FIELDS): a name the
 * forms take, plus a valid R2 code for a Spanish supplier or an EECS account of a listed destination
 * for an export. CnmcDestinationEditor and Send's CnmcSendPanel show each failed check as a field
 * error and isCnmcRecipientComplete requires them all, so they cannot drift apart.
 */
export const cnmcRecipientChecks = (
  route: CnmcRoute,
  destinations: readonly { region: string }[]
): Partial<Record<CnmcRecipientField, Record<string, CnmcRecipientCheck>>> => {
  const name = {
    maxLength: {
      test: (value: string) => value.trim().length <= CNMC_RECIPIENT_NAME_MAX,
      message: "validations.maxLength",
      params: { max: CNMC_RECIPIENT_NAME_MAX },
    },
  };
  if (route === "DOMESTIC")
    return {
      name,
      r2: { format: { test: isValidR2, message: "cnmc.validation.r2" } },
    };
  return {
    name,
    // A pasted account often carries spaces around it; they are trimmed when it is filed.
    eecs: {
      format: {
        test: (value: string) => isEecsAccountIdentifier(value.trim()),
        message: "validations.eecs_account_format",
      },
      // Left to the format check for a malformed account, so it is not reported twice.
      destination: {
        test: (value: string) =>
          !isEecsAccountIdentifier(value.trim()) ||
          exportDestinationOf(value, destinations) !== null,
        message: "cnmc.validation.eecs_destination_unsupported",
      },
    },
  };
};

/**
 * The registry region an export recipient's EECS account names, unless it is Spain's own; "" for
 * none. The EECS prefix names the destination registry, so export recipients have no registry picker.
 */
const exportRegionOf = (eecs: string): string => {
  const region = getRegistryRegionFromEecs(normalizeCnmcCode(eecs));
  return region && region !== "ES" ? region : "";
};

/** The CNMC export destination a typed EECS account names: its region when CNMC lists it, else null. */
export const exportDestinationOf = (
  eecs: string | null | undefined,
  destinations: readonly { region: string }[]
): string | null => {
  const region = exportRegionOf(eecs ?? "");
  return destinations.some((destination) => destination.region === region)
    ? region
    : null;
};

/** Whether a recipient can be filed: every field its route files is filled in and passes its checks. */
export const isCnmcRecipientComplete = (
  draft: CnmcRecipientDraft,
  destinations: readonly { region: string }[]
): boolean => {
  const checks = cnmcRecipientChecks(draft.route, destinations);
  return CNMC_RECIPIENT_FIELDS[draft.route].every((field) => {
    const value = draft[field];
    return (
      value.trim() !== "" &&
      Object.values(checks[field] ?? {}).every((check) => check.test(value))
    );
  });
};

/** The draft of a saved recipient: Spain's region is a domestic transfer, any other an export. */
export const recipientDraftOf = (
  recipient: CnmcRecipient
): CnmcRecipientDraft => ({
  route: recipient.region === "ES" ? "DOMESTIC" : "EXPORT",
  name: recipient.name,
  r2: recipient.r2 ?? "",
  eecs: recipient.eecs ?? "",
});

/**
 * The recipient to save: Spain with the R2 code for a domestic transfer, or the EECS account with the
 * region it names for an export. The region derives from the EECS alone, so it is right even before
 * the destinations loaded; the backend checks that CNMC exports there.
 */
export const toRecipient = (draft: CnmcRecipientDraft): CnmcRecipient =>
  draft.route === "DOMESTIC"
    ? { name: draft.name, region: "ES", r2: draft.r2 }
    : {
        name: draft.name,
        region: exportRegionOf(draft.eecs),
        eecs: draft.eecs.trim(),
      };
